import React, { memo, useCallback, useEffect, useRef } from 'react';
import { isEqual } from 'lodash-es';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PersonalizedSearchModel, PersonalizedSearchPaths } from 'client/data/models/personalized-search';
import { getQuery } from 'client/utils/location';
import { getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';

export const withModelFiltersUpdate =
  ({ shouldUpdateModelOnInit = true }) =>
  WrappedComponent => {
    const Component = memo(props => {
      const { componentParams, setModelValue, onUpdateFilters, ...rest } = props;
      const currentFiltersFromLocation = useRef();
      const isInventoryFiltered = useRef(false);

      useEffect(() => {
        if (componentParams) {
          const filters = getFilterFromLocation(getQuery(window.location));
          isInventoryFiltered.current = false;
          currentFiltersFromLocation.current = filters;

          if (shouldUpdateModelOnInit) {
            setModelValue(PersonalizedSearchPaths.getSelectedFilters(), PersonalizedSearchModel, filters);
          }
        }

        return () => {
          if (shouldUpdateModelOnInit) {
            setModelValue(PersonalizedSearchPaths.getSelectedFilters(), PersonalizedSearchModel, null);
          }
        };
      }, [componentParams, setModelValue]);

      const handleUpdateFilters = useCallback(
        filters => {
          setModelValue(PersonalizedSearchPaths.getSelectedFilters(), PersonalizedSearchModel, filters);
          isInventoryFiltered.current = true;
          currentFiltersFromLocation.current = filters;
          onUpdateFilters?.(filters);
        },
        [onUpdateFilters, setModelValue]
      );

      // Controlling the behavior of the browser's back button
      const onPopstate = useCallback(() => {
        const filters = getFilterFromLocation(getQuery(window.location));

        if (isInventoryFiltered.current && !isEqual(currentFiltersFromLocation.current, filters)) {
          handleUpdateFilters(filters);
        }
      }, [handleUpdateFilters]);

      useEffect(() => {
        window.addEventListener('popstate', onPopstate);
        return () => window.removeEventListener('popstate', onPopstate);
      }, [onPopstate]);

      return <WrappedComponent {...rest} onUpdateFilters={handleUpdateFilters} componentParams={componentParams} />;
    });

    Component.displayName = `withModelFiltersUpdate(${WrappedComponent.name || WrappedComponent.displayName})`;

    return connectToModel(Component, {});
  };
